import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';

import { useStoreActions } from '~/store/hooks';
import findValueByClientId from '../helpers/findValueByClientId';

interface IDateRange {
  from: string;
  to: string;
}

interface IProps {
  dateRange?: IDateRange;
  teamId: number;
  clientsList: Record<string, number[] | undefined>;
  clinicId: string;
  assignedTeams: Record<string, string>;
  clientAllocationClientsIds?: number[];
  enabled?: boolean;
}

const useClientAllocationAvailability = ({
  dateRange,
  clientsList,
  assignedTeams,
  teamId,
  clinicId,
  clientAllocationClientsIds = [],
  enabled = true,
}: IProps): void => {
  const list = Object.keys(clientsList);
  const clients = Object.values(clientsList).flat();
  const assignedTeamsIds = Object.values(assignedTeams);

  const prevClientsLengthRef = useRef(clients?.length);
  const prevTeamIdsRef = useRef(assignedTeamsIds);
  const prevDateRangeRef = useRef(dateRange);

  const { onValidateAvailability } = useStoreActions(actions => actions.clientAllocation);

  useEffect(() => {
    const isClientsLengthChanged = clients?.length !== prevClientsLengthRef.current;
    const isAssignedTeamsLengthChanged = !isEqual(prevTeamIdsRef.current, assignedTeamsIds);
    const isDateRangeChanged = !isEqual(prevDateRangeRef.current, dateRange);

    if (
      dateRange?.from &&
      dateRange?.to &&
      list?.length > 0 &&
      (isClientsLengthChanged || isDateRangeChanged || isAssignedTeamsLengthChanged)
    ) {
      const normalizePayload = list.flatMap(key => {
        const listOfClients = clientAllocationClientsIds.length
          ? clientsList[key as keyof typeof clientsList].filter(
              client => !clientAllocationClientsIds.includes(client),
            )
          : clientsList[key as keyof typeof clientsList];

        return listOfClients.map(client => {
          const clientTeamId = findValueByClientId(assignedTeams, client);
          const assignedTeamId = client && clientTeamId ? clientTeamId : teamId;
          return {
            teamId: Number(assignedTeamId),
            userId: Number(key.split('_')[1]),
            clientId: client,
          };
        });
      });

      if (normalizePayload.some(item => !item.teamId) || !enabled) {
        return;
      }

      onValidateAvailability({
        clinicId: Number(clinicId),
        payload: {
          dateRange: {
            from: dateRange.from,
            to: dateRange.to,
          },
          clientAllocations: normalizePayload,
        },
      });
    }

    prevClientsLengthRef.current = clients?.length;
    prevDateRangeRef.current = dateRange;
    prevTeamIdsRef.current = assignedTeamsIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, clients?.length, assignedTeamsIds?.length]);
};

export default useClientAllocationAvailability;
